import { useState, useEffect } from "react";
import { ModuleTabs } from '@/components/ui/ModuleTabs';
import { useLanguage } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { useChipWriter } from "@/hooks/useChipWriter";
import { useQueryClient, useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { ClipboardList, Radio, Loader2, Play, CheckCircle, Pause, AlertTriangle, CloudOff, RefreshCw, Wind, Box, BookOpen, MapPin, Hand } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { differenceInMinutes } from "date-fns";
import { useNFCReader } from "@/hooks/useNFCReader";
import { IncidentReportDialog } from "@/components/Processes/IncidentReportDialog";
import { LaminationPlybookIntegration } from "@/components/Operator";
import { ProcessDetailsDialog } from "@/components/Operator/ProcessDetailsDialog";
import { useOperatorProcessesOffline, useUpdateProcessOffline, useAvailablePoolWork, useClaimPoolWork } from "@/hooks/useWorkOrdersOffline";
import { useSyncState } from "@/hooks/useSyncState";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { api } from "@/lib/apiClient";

// Helper para cargar molds y plybooks por IDs
const useMoldMap = (moldIds: string[]) => {
  return useQuery({
    queryKey: ['mold-map', moldIds.join(',')],
    queryFn: async () => {
      if (!moldIds.length) return {};
      const uniqueIds = [...new Set(moldIds.filter(Boolean))];
      if (!uniqueIds.length) return {};
      const { data } = await api.from('molds').select('id, name, code, location').in('id', uniqueIds);
      return Object.fromEntries((data || []).map((m: any) => [m.id, m]));
    },
    enabled: moldIds.length > 0,
    staleTime: 60000,
  });
};

const usePlybookMap = (plybookIds: string[]) => {
  return useQuery({
    queryKey: ['plybook-map', plybookIds.join(',')],
    queryFn: async () => {
      if (!plybookIds.length) return {};
      const uniqueIds = [...new Set(plybookIds.filter(Boolean))];
      if (!uniqueIds.length) return {};
      const { data } = await api.from('plybooks').select('id,name,code').in('id', uniqueIds);
      return Object.fromEntries((data || []).map((p: any) => [p.id, p]));
    },
    enabled: plybookIds.length > 0,
    staleTime: 60000,
  });
};
import logger from '@/lib/logger';
import { notifyVacuumCompleted } from "@/lib/notificationService";

// Tiempo mínimo de vacío en minutos
const VACUUM_MIN_TIME_MINUTES = 15;

export default function OperatorView() {
  const { t } = useLanguage();
  const { user, profile, userRole } = useAuth();
  const { writeChip } = useChipWriter();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showNFCDialog, setShowNFCDialog] = useState(false);
  const [showIncidentDialog, setShowIncidentDialog] = useState(false);
  const [selectedWorkOrderForIncident, setSelectedWorkOrderForIncident] = useState<string | undefined>();
  const [isProcessingScan, setIsProcessingScan] = useState(false);
  const { isScanning, isSupported, lastRead, startScanning, stopScanning, writeToTag } = useNFCReader();

  // Estados para proceso de vacío
  const [showVacuumDialog, setShowVacuumDialog] = useState(false);
  const [showMinTimeWarning, setShowMinTimeWarning] = useState(false);
  const [vacuumPendingRecord, setVacuumPendingRecord] = useState<any>(null);
  const [vacuumData, setVacuumData] = useState({ pressure: "", observations: "" });
  const [vacuumElapsedMinutes, setVacuumElapsedMinutes] = useState(0);

  // Estados para proceso de laminado con plybook
  const [showLaminationView, setShowLaminationView] = useState(false);
  const [laminationWorkOrderId, setLaminationWorkOrderId] = useState<string | null>(null);
// Estados para diálogo de detalles del proceso
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [selectedRecordForDetails, setSelectedRecordForDetails] = useState<any>(null);

  // Use offline-capable hooks
  const { data: processRecords, isLoading, isOfflineData, refetch } = useOperatorProcessesOffline();
  const { data: poolWork, isLoading: isLoadingPool, refetch: refetchPool } = useAvailablePoolWork();

  // Cargar molds y plybooks por separado (evita embeds anidados que fallan)
  const moldIds = [...(processRecords || []), ...(poolWork || [])].map((r: any) => r.work_orders?.mold_id).filter(Boolean);
  const plybookIds = [...(processRecords || []), ...(poolWork || [])].map((r: any) => r.work_orders?.plybook_id).filter(Boolean);
  const { data: moldMap = {} } = useMoldMap(moldIds);
  const { data: plybookMap = {} } = usePlybookMap(plybookIds);
  const claimWork = useClaimPoolWork();
  const { isOnline, pendingCount } = useSyncState();
  const updateProcess = useUpdateProcessOffline();
  const canManualActions = userRole === 'admin' || userRole === 'supervisor' || userRole === 'operator';
  const canWriteTags = userRole === 'admin' || userRole === 'supervisor';

  // Helpers para identificar procesos especiales
  const isVacuumProcess = (record: any) =>
    record?.manufacturing_processes?.name === 'vacuum_bagging';

  const isCuringProcess = (record: any) =>
    record?.manufacturing_processes?.name === 'autoclave_curing';

  const isLaminationProcess = (record: any) =>
    record?.manufacturing_processes?.name === 'material_layup' || record?.manufacturing_processes?.name === 'Laminado' ||
    record?.manufacturing_processes?.code === 'LAMI';

  // Verificar si la OT tiene molde asignado (requerido para laminado)
  const checkMoldAssigned = async (workOrderId: string): Promise<{ hasMold: boolean; moldName?: string }> => {
    try {
      const { data, error } = await api
        .from("work_orders")
        .select("mold_id, mold:molds(name)")
        .eq("id", workOrderId)
        .single();

      if (error) throw error;
      return {
        hasMold: !!data?.mold_id,
        moldName: (data?.mold as any)?.name,
      };
    } catch (error) {
      logger.error("Error checking mold assignment:", error);
      return { hasMold: false };
    }
  };

  // Verificar si el vacío está completado para una orden específica
  const isVacuumCompletedForOrder = async (workOrderId: string) => {
    try {
      const { data, error } = await api
        .from("process_records")
        .select("id, status, manufacturing_processes!inner(name)")
        .eq("work_order_id", workOrderId)
        .eq("manufacturing_processes.name", "vacuum_bagging")
        .maybeSingleRow();

      if (error) throw error;
      return data?.status === "completed";
    } catch (error) {
      logger.error("Error checking vacuum status:", error);
      return false;
    }
  };

  // Obtener tiempo transcurrido en minutos
  const getVacuumElapsedMinutes = (record: any) => {
    if (!record?.started_at) return 0;
    return differenceInMinutes(new Date(), new Date(record.started_at));
  };

  const handleProcessClick = (record: any) => {
    // Mostrar dialogo de detalles del proceso
    setSelectedRecordForDetails(record);
    setShowDetailsDialog(true);
  };

  const handleNFCScan = async () => {
    if (!isSupported) {
      toast.error("NFC no está soportado. Disponible solo en Android con Chrome.");
      return;
    }
    setShowNFCDialog(true);
    await startScanning();
  };

  const handleStopScanning = () => {
    stopScanning();
    setShowNFCDialog(false);
  };

  const extractTagPayload = (data: any) => {
    if (!data || typeof data !== "object") {
      return { workOrderId: "", processId: "", moldId: "", tagType: "" };
    }

    return {
      workOrderId:
        data.workOrderId ||
        data.work_order_id ||
        data.workOrder ||
        data.orderId ||
        data.order_id ||
        data.otId ||
        data.ot_id ||
        "",
      processId:
        data.processId ||
        data.process_id ||
        data.processRecordId ||
        data.process_record_id ||
        "",
      moldId:
        data.moldId ||
        data.mold_id ||
        data.assigned_to_mold_id ||
        data.mold ||
        "",
      tagType: data.tagType || data.tag_type || data.type || "",
    };
  };

  const showPlotterTagInfo = (tagInfo: any) => {
    if (!tagInfo || tagInfo.tag_type !== "plotter_cut" || !tagInfo.notes) return;

    try {
      const payload = JSON.parse(tagInfo.notes);
      if (payload?.type !== "plotter_cut" || !payload.package) return;

      const pkg = payload.package;
      const details = [
        pkg.material_type,
        pkg.total_layers ? `${pkg.total_layers} capas` : null,
        pkg.batch_number,
      ]
        .filter(Boolean)
        .join(" • ");

      toast.info(`Pieza: ${pkg.name}${details ? ` (${details})` : ""}`, {
        duration: 4000,
      });
    } catch (error) {
      logger.error("Error parsing NFC tag notes:", error);
    }
  };

  const showMoldTagInfo = (moldInfo: any) => {
    if (!moldInfo) return;
    const labelParts = [moldInfo.code, moldInfo.name].filter(Boolean);
    const label = labelParts.length > 0 ? labelParts.join(" - ") : moldInfo.id;
    const location = moldInfo.location ? ` (${moldInfo.location})` : "";
    toast.info(`Molde: ${label}${location}`, { duration: 4000 });
  };

  const recordTraceabilityEvent = async ({
    workOrderId,
    eventType,
    description,
    metadata,
    location,
  }: {
    workOrderId: string;
    eventType: string;
    description?: string | null;
    metadata?: Record<string, any>;
    location?: string | null;
  }) => {
    if (!workOrderId) return;

    try {
      await api.from("traceability_events").insert({
        work_order_id: workOrderId,
        event_type: eventType,
        description: description || null,
        location: location || null,
        metadata: metadata || {},
        performed_by: user?.id || null,
      });
    } catch (error) {
      logger.error("Error logging traceability event:", error);
    }
  };

  const fetchTagAssignment = async (tagUid: string) => {
    try {
      const { data, error } = await api
        .from("nfc_tags")
        .select(
          "tag_uid, tag_label, work_order_id, process_id, assigned_to_mold_id, location, tag_type, description, notes, molds:assigned_to_mold_id ( id, name, code, location, status )"
        )
        .eq("tag_uid", tagUid)
        .maybeSingleRow();

      if (error) throw error;
      return data;
    } catch (error) {
      logger.error("Error fetching NFC tag assignment:", error);
      return null;
    }
  };

  const fetchMoldInfo = async (moldId: string) => {
    if (!moldId) return null;
    try {
      const { data, error } = await api
        .from("molds")
        .select("id, name, code, location, status")
        .eq("id", moldId)
        .maybeSingleRow();

      if (error) throw error;
      return data;
    } catch (error) {
      logger.error("Error fetching mold info:", error);
      return null;
    }
  };

  const updateTagLastScan = async (tagUid: string) => {
    if (!tagUid) return;
    try {
      await api
        .from("nfc_tags")
        .update({
          last_scanned_at: new Date().toISOString(),
          last_scanned_by: user?.id || null,
        })
        .eq("tag_uid", tagUid);
    } catch (error) {
      logger.error("Error updating tag last scan:", error);
    }
  };

  const logNfcCapture = async ({
    tagUid,
    tagLabel,
    workOrderId,
    processId,
    moldId,
    moldInfo,
    tagType,
    rawData,
    location,
  }: {
    tagUid: string;
    tagLabel?: string | null;
    workOrderId?: string | null;
    processId?: string | null;
    moldId?: string | null;
    moldInfo?: { id?: string | null; name?: string | null; code?: string | null } | null;
    tagType?: string | null;
    rawData?: any;
    location?: string | null;
  }) => {
    if (!user?.id) return;
    try {
      await api.from("nfc_captures").insert({
        capture_id: `CAP-${Date.now().toString(36).toUpperCase()}`,
        device_id: navigator.userAgent.slice(0, 50),
        operator_id: user.id,
        location: location || "Sin ubicacion",
        status: "completed",
        data_points: {
          serial_number: tagUid,
          tag_uid: tagUid,
          tag_label: tagLabel || null,
          work_order_id: workOrderId || null,
          process_id: processId || null,
          mold_id: moldId || null,
          mold_name: moldInfo?.name || null,
          mold_code: moldInfo?.code || null,
          tag_type: tagType || null,
          raw_data: rawData || null,
        },
        tag_uid: tagUid,
        work_order_id: workOrderId || null,
        process_record_id: null,
      });
    } catch (error) {
      logger.error("Error logging NFC capture:", error);
    }
  };

  const fetchProcessRecords = async (workOrderId: string) => {
    const { data, error } = await api
      .from("process_records")
      .select(
        `id, status, process_id, started_at, completed_at, operator_id,
         manufacturing_processes(id, name, code, sequence_order, process_order)`
      )
      .eq("work_order_id", workOrderId);

    if (error) throw error;
    return data || [];
  };

  const ensureProcessRecords = async (workOrderId: string) => {
    let records = await fetchProcessRecords(workOrderId);
    if (records.length > 0) return records;

    const { data: processes, error } = await api
      .from("manufacturing_processes")
      .select("id, name, sequence_order, process_order, is_active")
      .eq("is_active", true)
      .order("sequence_order");

    if (error) throw error;
    if (!processes || processes.length === 0) return [];

    const payload = processes.map((process) => ({
      work_order_id: workOrderId,
      process_id: process.id,
      status: "pending" as const,
    }));

    const { error: insertError } = await api
      .from("process_records")
      .insert(payload);

    if (insertError) throw insertError;
    records = await fetchProcessRecords(workOrderId);
    return records;
  };

  const sortRecordsByOrder = (records: any[]) => {
    return [...records].sort((a, b) => {
      const aOrder = a.manufacturing_processes?.sequence_order ?? a.manufacturing_processes?.process_order ?? 0;
      const bOrder = b.manufacturing_processes?.sequence_order ?? b.manufacturing_processes?.process_order ?? 0;
      return aOrder - bOrder;
    });
  };

  const startProcessRecord = async (record: any, startedAt: string): Promise<boolean> => {
    // Validar molde para proceso de laminado
    if (isLaminationProcess(record) && record.work_order_id) {
      const { hasMold } = await checkMoldAssigned(record.work_order_id);
      if (!hasMold) {
        toast.error("Molde requerido", {
          description: "Debes asignar un molde a la orden antes de iniciar el laminado",
        });
        return false;
      }

      // Abrir vista de laminado con plybook integration
      setLaminationWorkOrderId(record.work_order_id);
      setShowLaminationView(true);
    }

    const updates: any = { status: "in_progress" };
    if (!record.started_at) {
      updates.started_at = startedAt;
    }
    if (!record.operator_id && user?.id) {
      updates.operator_id = user.id;
    }

    const { error } = await api
      .from("process_records")
      .update(updates)
      .eq("id", record.id);

    if (error) {
      console.error("Error starting process:", error);
      toast.error("Error al iniciar proceso");
      return false;
    }
    return true;
  };

  const completeProcessRecord = async (record: any, completedAt: string, opts?: { tagUid?: string; workOrderNumber?: string }) => {
    const updates: any = {
      status: "completed",
      completed_at: completedAt,
    };

    if (record.started_at) {
      const durationMs = new Date(completedAt).getTime() - new Date(record.started_at).getTime();
      if (durationMs > 0) {
        updates.duration_minutes = Math.round(durationMs / 60000);
      }
    }

    const { error } = await api
      .from("process_records")
      .update(updates)
      .eq("id", record.id);

    if (error) throw error;

    // Reescribir chip NFC con resumen del paso recién completado (sólo si hay tag asignado)
    if (opts?.tagUid) {
      const procName = record.manufacturing_processes?.name || record.process_name || "step";
      const procSeq = record.manufacturing_processes?.sequence_order || record.sequence_order;
      try {
        await writeChip({
          tagUid: opts.tagUid,
          processRecordId: record.id,
          payload: {
            wo: opts.workOrderNumber,
            step: procName,
            step_seq: procSeq,
            status: "completed",
            at: completedAt,
            op: profile?.full_name || user?.email?.split("@")[0],
          },
        });
      } catch (e) {
        // Degradación elegante: el process_record ya está completed, sólo falla la escritura del chip
        logger.warn("Chip rewrite failed for process completion", e);
      }
    }
  };

  const fetchActiveProcessRecord = async (workOrderId: string) => {
    const { data, error } = await api
      .from("process_records")
      .select("id, operator_id, status")
      .eq("work_order_id", workOrderId)
      .in("status", ["in_progress", "paused"])
      .order("created_at", { ascending: true })
      .limit(1)
      .maybeSingleRow();

    if (error) throw error;
    return data || null;
  };

  const createWorkOrderConflictAlert = async ({
    workOrder,
    assignedOperatorId,
    source,
  }: {
    workOrder: { id: string; order_number?: string | null };
    assignedOperatorId: string;
    source: "nfc" | "manual";
  }) => {
    if (!user?.id) return;

    const operatorIds = [user.id, assignedOperatorId].filter(Boolean);
    let operatorNames: Record<string, string> = {};
    try {
      const { data: operators } = await api
        .from("profiles")
        .select("id, full_name")
        .in("id", operatorIds);
      operatorNames = (operators || []).reduce<Record<string, string>>((acc, op: any) => {
        acc[op.id] = op.full_name || op.id;
        return acc;
      }, {});
    } catch (error) {
      logger.error("Error fetching operator names:", error);
    }

    const attemptedBy = operatorNames[user.id] || profile?.full_name || user.email || user.id;
    const assignedTo = operatorNames[assignedOperatorId] || assignedOperatorId;
    const orderLabel = workOrder.order_number || workOrder.id;

    const alertMessage = `Intento de iniciar OT ${orderLabel} por ${attemptedBy}. Asignada a ${assignedTo}. Origen: ${source}.`;

    try {
      await api.from("staff_alerts").insert({
        title: "Conflicto de operario en OT",
        message: alertMessage,
        alert_type: "work_order_conflict",
        priority: "high",
        target_roles: ["admin", "supervisor"],
        target_all: false,
        created_by: user.id,
        requires_confirmation: false,
      });
    } catch (error) {
      logger.error("Error creating work order conflict alert:", error);
    }
  };

  const ensureWorkOrderStarted = async (
    workOrderId: string,
    source: "nfc" | "manual",
    activeOperatorId?: string | null
  ): Promise<boolean> => {
    try {
      const { data, error } = await api
        .from("work_orders")
        .select("id, status, started_at, order_number, assigned_operator")
        .eq("id", workOrderId)
        .single();

      if (error) throw error;
      if (!user?.id) return false;

      if (data?.assigned_operator && data.assigned_operator !== user.id) {
        await createWorkOrderConflictAlert({
          workOrder: data,
          assignedOperatorId: data.assigned_operator,
          source,
        });
        toast.error("Orden asignada a otro operario");
        return false;
      }

      if (activeOperatorId && activeOperatorId !== user.id) {
        if (!data?.assigned_operator) {
          await api
            .from("work_orders")
            .update({ assigned_operator: activeOperatorId })
            .eq("id", workOrderId)
            .is("assigned_operator", null);
        }
        await createWorkOrderConflictAlert({
          workOrder: data,
          assignedOperatorId: activeOperatorId,
          source,
        });
        toast.error("Orden en curso por otro operario");
        return false;
      }

      if (!data?.assigned_operator && user?.id) {
        await api
          .from("work_orders")
          .update({ assigned_operator: user.id })
          .eq("id", workOrderId)
          .is("assigned_operator", null);
      }

      if (data?.status === "pending") {
        await api
          .from("work_orders")
          .update({
            status: "in_progress",
            started_at: data.started_at || new Date().toISOString(),
          })
          .eq("id", workOrderId);
      }
      return true;
    } catch (error) {
      logger.error("Error updating work order status:", error);
      return false;
    }
  };

  const completeWorkOrderIfDone = async (workOrderId: string) => {
    try {
      const { data, error } = await api
        .from("process_records")
        .select("status")
        .eq("work_order_id", workOrderId);

      if (error) throw error;
      if (!data || data.length === 0) return;

      const allCompleted = data.every((record: any) => record.status === "completed");
      if (!allCompleted) return;

      await api
        .from("work_orders")
        .update({
          status: "completed",
          completed_at: new Date().toISOString(),
        })
        .eq("id", workOrderId);
    } catch (error) {
      logger.error("Error completing work order:", error);
    }
  };

  const createProcessAlert = async ({
    record,
    workOrderId,
    tagUid,
    source,
  }: {
    record: any;
    workOrderId: string;
    tagUid?: string | null;
    source: "nfc" | "manual";
  }) => {
    const processName = record.manufacturing_processes?.name || "Proceso";
    const processId = record.process_id || record.manufacturing_process_id || null;
    toast.warning(`Alerta: se cerrara ${processName}`);
    await recordTraceabilityEvent({
      workOrderId,
      eventType: "process_alert",
      description: `Alerta previa al cierre de ${processName}`,
      metadata: {
        source,
        process_record_id: record.id,
        process_id: processId,
        tag_uid: tagUid || null,
      },
    });
  };

  const handleNfcProcessFlow = async ({
    workOrderId,
    processId,
    tagUid,
    rawData,
    tagInfo,
  }: {
    workOrderId: string;
    processId?: string | null;
    tagUid: string;
    rawData?: any;
    tagInfo?: any;
  }) => {
    const timestamp = new Date().toISOString();
    const records = await ensureProcessRecords(workOrderId);

    if (records.length === 0) {
      toast.error("No hay procesos configurados para esta orden");
      return;
    }

    const sortedRecords = sortRecordsByOrder(records);
    const currentActive = sortedRecords.find((record) =>
      ["in_progress", "paused"].includes(record.status)
    );

    const canProceed = await ensureWorkOrderStarted(
      workOrderId,
      "nfc",
      currentActive?.operator_id || null
    );
    if (!canProceed) return;

    if (!processId) {
      if (currentActive) {
        toast.info("La orden ya tiene un proceso en curso o pausado");
        return;
      }

      const nextRecord = sortedRecords.find((record) =>
        ["pending", "paused"].includes(record.status)
      );

      if (!nextRecord) {
        toast.info("No hay procesos pendientes para iniciar");
        return;
      }

      toast.info("Estás a punto de iniciar este proceso");
      const started = await startProcessRecord(nextRecord, timestamp);
      if (!started) return;
      toast.success("¡Proceso iniciado!");
      await recordTraceabilityEvent({
        workOrderId,
        eventType: "process_started",
        description: `Proceso iniciado: ${nextRecord.manufacturing_processes?.name || "Proceso"}`,
        metadata: {
          source: "nfc",
          process_record_id: nextRecord.id,
          process_id: nextRecord.process_id,
          tag_uid: tagUid,
        },
      });
      return;
    }

    let targetRecord =
      sortedRecords.find((record) => record.id === processId) ||
      sortedRecords.find((record) => record.process_id === processId);

    if (!targetRecord) {
      const { data: process, error } = await api
        .from("manufacturing_processes")
        .select("id, name, sequence_order, process_order")
        .eq("id", processId)
        .maybeSingleRow();

      if (error) throw error;

      if (process?.id) {
        // Validar molde si es proceso de laminado
        const isLamination = process.name === 'material_layup';
        if (isLamination) {
          const { hasMold } = await checkMoldAssigned(workOrderId);
          if (!hasMold) {
            toast.error("Molde requerido", {
              description: "Debes asignar un molde a la orden antes de iniciar el laminado",
            });
            return;
          }
        }

        const { data: createdRecord, error: createError } = await api
          .from("process_records")
          .insert({
            work_order_id: workOrderId,
            process_id: process.id,
            status: "in_progress",
            started_at: timestamp,
            operator_id: user?.id || null,
          })
          .select("id, status, process_id, started_at, completed_at, operator_id")
          .single();

        if (createError) throw createError;
        targetRecord = {
          ...createdRecord,
          manufacturing_processes: process,
        };
      }
    }

    if (!targetRecord) {
      toast.error("No se encontro el proceso para esta etiqueta");
      return;
    }

    if (currentActive && currentActive.id !== targetRecord.id) {
      await createProcessAlert({
        record: currentActive,
        workOrderId,
        tagUid,
        source: "nfc",
      });
      await completeProcessRecord(currentActive, timestamp, { tagUid, workOrderNumber: currentActive.work_orders?.order_number });
      await recordTraceabilityEvent({
        workOrderId,
        eventType: "process_completed",
        description: `Proceso completado: ${currentActive.manufacturing_processes?.name || "Proceso"}`,
        metadata: {
          source: "nfc",
          process_record_id: currentActive.id,
          process_id: currentActive.process_id,
          tag_uid: tagUid,
        },
      });
    }

    if (targetRecord.status === "completed") {
      toast.info("Este proceso ya fue completado");
      return;
    }

    if (targetRecord.status === "in_progress") {
      const nextPending = sortedRecords.find((record) =>
        ["pending", "paused"].includes(record.status)
      );
      if (!nextPending) {
        await createProcessAlert({
          record: targetRecord,
          workOrderId,
          tagUid,
          source: "nfc",
        });
        await completeProcessRecord(targetRecord, timestamp, { tagUid, workOrderNumber: targetRecord.work_orders?.order_number });
        toast.success("Proceso finalizado");
        await recordTraceabilityEvent({
          workOrderId,
          eventType: "process_completed",
          description: `Proceso completado: ${targetRecord.manufacturing_processes?.name || "Proceso"}`,
          metadata: {
            source: "nfc",
            process_record_id: targetRecord.id,
            process_id: targetRecord.process_id,
            tag_uid: tagUid,
          },
        });
        await completeWorkOrderIfDone(workOrderId);
      } else {
        toast.info("Proceso ya en curso");
      }
    } else {
      toast.info("Estás a punto de iniciar este proceso");
      const started = await startProcessRecord(targetRecord, timestamp);
      if (!started) return;
      toast.success("¡Proceso iniciado!");
      await recordTraceabilityEvent({
        workOrderId,
        eventType: "process_started",
        description: `Proceso iniciado: ${targetRecord.manufacturing_processes?.name || "Proceso"}`,
        metadata: {
          source: "nfc",
          process_record_id: targetRecord.id,
          process_id: targetRecord.process_id,
          tag_uid: tagUid,
        },
      });
    }

  };

  // Process NFC read data
  useEffect(() => {
    if (!lastRead || isProcessingScan) return;

    const processScan = async () => {
      setIsProcessingScan(true);
      try {
        const { workOrderId, processId, moldId, tagType } = extractTagPayload(lastRead.data);
        let resolvedWorkOrderId = workOrderId;
        let resolvedProcessId = processId;
        let resolvedMoldId = moldId;

        let tagInfo = null;
        if (!resolvedWorkOrderId || !resolvedProcessId || !resolvedMoldId) {
          tagInfo = await fetchTagAssignment(lastRead.serialNumber);
          resolvedWorkOrderId = resolvedWorkOrderId || tagInfo?.work_order_id || "";
          resolvedProcessId = resolvedProcessId || tagInfo?.process_id || "";
          resolvedMoldId = resolvedMoldId || tagInfo?.assigned_to_mold_id || "";
        }

        const resolvedTagType = tagType || tagInfo?.tag_type || "";
        const isMoldTag = resolvedTagType === "mold" || Boolean(resolvedMoldId);
        const isPlotterTag = resolvedTagType === "plotter_cut";

        if (!resolvedWorkOrderId && (isMoldTag || isPlotterTag)) {
          if (isPlotterTag) {
            showPlotterTagInfo(tagInfo);
          }

          let moldInfo = tagInfo?.molds;
          if (isMoldTag && !moldInfo && resolvedMoldId) {
            moldInfo = await fetchMoldInfo(resolvedMoldId);
          }
          if (isMoldTag) {
            showMoldTagInfo(moldInfo);
          }

          await updateTagLastScan(lastRead.serialNumber);
          await logNfcCapture({
            tagUid: lastRead.serialNumber,
            tagLabel: tagInfo?.tag_label || null,
            workOrderId: null,
            processId: null,
            moldId: resolvedMoldId || null,
            moldInfo: moldInfo || null,
            tagType: resolvedTagType || null,
            rawData: lastRead.data,
            location: tagInfo?.location || moldInfo?.location || null,
          });
          return;
        }

        if (!resolvedWorkOrderId) {
          toast.error("Etiqueta NFC sin orden de trabajo");
          return;
        }

        showPlotterTagInfo(tagInfo);

        await updateTagLastScan(lastRead.serialNumber);
        await logNfcCapture({
          tagUid: lastRead.serialNumber,
          tagLabel: tagInfo?.tag_label || null,
          workOrderId: resolvedWorkOrderId,
          processId: resolvedProcessId || null,
          moldId: resolvedMoldId || null,
          moldInfo: tagInfo?.molds || null,
          tagType: resolvedTagType || null,
          rawData: lastRead.data,
          location: tagInfo?.location || null,
        });
        await handleNfcProcessFlow({
          workOrderId: resolvedWorkOrderId,
          processId: resolvedProcessId || null,
          tagUid: lastRead.serialNumber,
          rawData: lastRead.data,
          tagInfo,
        });

        queryClient.invalidateQueries({ queryKey: ["operator-processes"] });
      } catch (error: any) {
        logger.error("Error processing NFC scan:", error);
        toast.error("Error al procesar la etiqueta NFC");
      } finally {
        setIsProcessingScan(false);
        setShowNFCDialog(false);
        stopScanning({ silent: true }); // No mostrar toast "detenido" después de procesar
      }
    };

    processScan();
  }, [lastRead, isProcessingScan]);

  const handleProcessAction = async (record: any) => {
    try {
      let newStatus: string;
      let updateData: any = {};

      if (record.operator_id && user?.id && record.operator_id !== user.id) {
        await createWorkOrderConflictAlert({
          workOrder: { id: record.work_order_id, order_number: record.work_orders?.order_number },
          assignedOperatorId: record.operator_id,
          source: "manual",
        });
        toast.error("Proceso asignado a otro operario");
        return;
      }

      const activeRecord = await fetchActiveProcessRecord(record.work_order_id);
      if (activeRecord && activeRecord.id !== record.id) {
        await ensureWorkOrderStarted(record.work_order_id, "manual", activeRecord.operator_id || null);
        toast.error("La orden ya tiene un proceso activo o pausado");
        return;
      }

      const canProceed = await ensureWorkOrderStarted(record.work_order_id, "manual", record.operator_id || null);
      if (!canProceed) return;

      if (record.status === "pending") {
        // Validación: no se puede iniciar curado sin completar vacío
        if (isCuringProcess(record)) {
          const vacuumDone = await isVacuumCompletedForOrder(record.work_order_id);
          if (!vacuumDone) {
            toast.error("⚠️ Debe completar el proceso de vacío antes de iniciar el curado");
            return;
          }
        }

        // Si es proceso de vacío, mostrar dialog para capturar datos
        if (isVacuumProcess(record)) {
          setVacuumPendingRecord(record);
          setShowVacuumDialog(true);
          return;
        }

        newStatus = "in_progress";
        updateData.started_at = new Date().toISOString();
        if (!record.operator_id && user?.id) {
          updateData.operator_id = user.id;
        }
      } else if (record.status === "in_progress") {
        // Validación de tiempo mínimo para vacío
        if (isVacuumProcess(record)) {
          const elapsedMinutes = getVacuumElapsedMinutes(record);
          if (elapsedMinutes < VACUUM_MIN_TIME_MINUTES) {
            setVacuumElapsedMinutes(elapsedMinutes);
            setVacuumPendingRecord(record);
            setShowMinTimeWarning(true);
            return;
          }
        }

        await createProcessAlert({
          record,
          workOrderId: record.work_order_id,
          source: "manual",
        });
        newStatus = "completed";
        updateData.completed_at = new Date().toISOString();
        if (record.started_at) {
          const durationMs = new Date(updateData.completed_at).getTime() - new Date(record.started_at).getTime();
          if (durationMs > 0) {
            updateData.duration_minutes = Math.round(durationMs / 60000);
          }
        }
      } else if (record.status === "paused") {
        newStatus = "in_progress";
      } else {
        toast.info("Este proceso ya está completado");
        return;
      }

      // Use offline-capable mutation
      await updateProcess.mutateAsync({
        recordId: record.id,
        updates: { status: newStatus, ...updateData },
      });

      // Show appropriate toast based on action
      if (newStatus === "in_progress" && record.status === "pending") {
        toast.success("¡Proceso iniciado!");
        await recordTraceabilityEvent({
          workOrderId: record.work_order_id,
          eventType: "process_started",
          description: `Proceso iniciado: ${record.manufacturing_processes?.name || "Proceso"}`,
          metadata: {
            source: "manual",
            process_record_id: record.id,
            process_id: record.process_id || record.manufacturing_process_id || null,
          },
        });
      } else if (newStatus === "completed") {
        // Notificación específica para vacío completado
        if (isVacuumProcess(record)) {
          let pressure = '';
          try {
            const notesData = JSON.parse(record.notes || '{}');
            pressure = notesData.pressure || '';
          } catch {}

          await notifyVacuumCompleted(
            profile?.full_name || user?.email || 'Operario',
            record.work_orders?.order_number || 'N/A',
            pressure
          );
          toast.success("✅ Vacío completado. El proceso de curado puede iniciarse.");
        } else {
          toast.success("¡Proceso completado!");
        }

        await recordTraceabilityEvent({
          workOrderId: record.work_order_id,
          eventType: "process_completed",
          description: `Proceso completado: ${record.manufacturing_processes?.name || "Proceso"}`,
          metadata: {
            source: "manual",
            process_record_id: record.id,
            process_id: record.process_id || record.manufacturing_process_id || null,
          },
        });
      } else if (newStatus === "in_progress" && record.status === "paused") {
        toast.success("¡Proceso reanudado!");
        await recordTraceabilityEvent({
          workOrderId: record.work_order_id,
          eventType: "process_started",
          description: `Proceso reanudado: ${record.manufacturing_processes?.name || "Proceso"}`,
          metadata: {
            source: "manual",
            process_record_id: record.id,
            process_id: record.process_id || record.manufacturing_process_id || null,
          },
        });
      }
    } catch (error: any) {
      logger.error("Error updating process:", error);
      toast.error("Error al actualizar el proceso");
    }
  };

  const handleWriteNFCTag = async (processId: string, workOrderId: string) => {
    await writeToTag({ processId, workOrderId, tagType: "process" });
  };

  // Iniciar proceso de vacío con datos capturados
  const handleVacuumStart = async () => {
    if (!vacuumPendingRecord) return;

    try {
      const notes = JSON.stringify({
        type: 'vacuum_data',
        pressure: vacuumData.pressure,
        observations: vacuumData.observations,
        started_by: profile?.full_name || user?.email,
      });

      const updateData: any = {
        status: "in_progress",
        started_at: new Date().toISOString(),
        notes,
      };

      if (!vacuumPendingRecord.operator_id && user?.id) {
        updateData.operator_id = user.id;
      }

      await updateProcess.mutateAsync({
        recordId: vacuumPendingRecord.id,
        updates: updateData,
      });

      toast.success("🔵 Vacío iniciado correctamente");
      await recordTraceabilityEvent({
        workOrderId: vacuumPendingRecord.work_order_id,
        eventType: "process_started",
        description: `Vacío iniciado con presión: ${vacuumData.pressure || 'N/A'}`,
        metadata: {
          source: "manual",
          process_record_id: vacuumPendingRecord.id,
          vacuum_pressure: vacuumData.pressure,
        },
      });

      // Reset estados
      setShowVacuumDialog(false);
      setVacuumPendingRecord(null);
      setVacuumData({ pressure: "", observations: "" });
    } catch (error: any) {
      logger.error("Error starting vacuum process:", error);
      toast.error("Error al iniciar el vacío");
    }
  };

  // Completar vacío forzadamente (antes del tiempo mínimo)
  const handleForceCompleteVacuum = async () => {
    if (!vacuumPendingRecord) return;

    try {
      const updateData: any = {
        status: "completed",
        completed_at: new Date().toISOString(),
      };

      if (vacuumPendingRecord.started_at) {
        const durationMs = new Date().getTime() - new Date(vacuumPendingRecord.started_at).getTime();
        if (durationMs > 0) {
          updateData.duration_minutes = Math.round(durationMs / 60000);
        }
      }

      await createProcessAlert({
        record: vacuumPendingRecord,
        workOrderId: vacuumPendingRecord.work_order_id,
        source: "manual",
      });

      await updateProcess.mutateAsync({
        recordId: vacuumPendingRecord.id,
        updates: updateData,
      });

      // Obtener presión de los notes
      let pressure = '';
      try {
        const notesData = JSON.parse(vacuumPendingRecord.notes || '{}');
        pressure = notesData.pressure || '';
      } catch {}

      await notifyVacuumCompleted(
        profile?.full_name || user?.email || 'Operario',
        vacuumPendingRecord.work_orders?.order_number || 'N/A',
        pressure
      );

      toast.success("✅ Vacío completado. El proceso de curado puede iniciarse.");
      await recordTraceabilityEvent({
        workOrderId: vacuumPendingRecord.work_order_id,
        eventType: "process_completed",
        description: `Vacío completado (tiempo: ${vacuumElapsedMinutes} min, mínimo: ${VACUUM_MIN_TIME_MINUTES} min)`,
        metadata: {
          source: "manual",
          process_record_id: vacuumPendingRecord.id,
          elapsed_minutes: vacuumElapsedMinutes,
          min_required: VACUUM_MIN_TIME_MINUTES,
          forced: true,
        },
      });

      // Reset estados
      setShowMinTimeWarning(false);
      setVacuumPendingRecord(null);
      setVacuumElapsedMinutes(0);
    } catch (error: any) {
      logger.error("Error completing vacuum process:", error);
      toast.error("Error al completar el vacío");
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const nfcScanCard = (
    <Card
      className="cursor-pointer hover:shadow-lg transition-all duration-200 hover:scale-105 bg-muted/50"
      onClick={handleNFCScan}
    >
      <CardContent className="p-6">
        <div className="flex flex-col items-center text-center gap-3">
          <Radio className="w-12 h-12 text-primary" />
          <h3 className="text-xl font-bold text-foreground">
            {t.operatorView.nfcScan}
          </h3>
          <p className="text-sm text-muted-foreground">
            {t.operatorView.scanNow}
          </p>
        </div>
      </CardContent>
    </Card>
  );

  // Vista de laminado con plybook integration
  if (showLaminationView && laminationWorkOrderId) {
    return (
      <LaminationPlybookIntegration
        workOrderId={laminationWorkOrderId}
        onBack={() => {
          setShowLaminationView(false);
          setLaminationWorkOrderId(null);
          refetch();
        }}
        onComplete={async () => {
          try {
            // 1. Buscar el proceso de laminado actual para esta OT
            const { data: lamiProcess } = await api
              .from('process_records')
              .select('id,work_order_id')
              .eq('work_order_id', laminationWorkOrderId)
              .eq('status', 'in_progress')
              .limit(1);
            
            if (lamiProcess && lamiProcess.length > 0) {
              // 2. Marcar el proceso de laminado como completado
              await api
                .from('process_records')
                .update({ status: 'completed', completed_at: new Date().toISOString() })
                .eq('id', lamiProcess[0].id);
            }
            
            // 3. Buscar el ID del proceso de vacío
            const { data: vacuumProcessType } = await api
              .from('manufacturing_processes')
              .select('id,name')
              .eq('code', 'vacuum_bagging')
              .limit(1);
            
            if (vacuumProcessType && vacuumProcessType.length > 0) {
              // 4. Crear el proceso de vacío asignado al operario actual
              const { data: newVacuum } = await api
                .from('process_records')
                .insert({
                  work_order_id: laminationWorkOrderId,
                  process_id: vacuumProcessType[0].id,
                  operator_id: user?.id || null,
                  status: 'pending',
                })
                .select('id')
                .single();
              
              // 5. Preguntar si continúa con vacío
              const continueVacuum = window.confirm(
                'Laminado completado. ¿Deseas continuar con el proceso de VACÍO? Pulsa Aceptar para continuar o Cancelar para asignarlo a otro operario.'
              );
              
              if (continueVacuum) {
                // Iniciar el proceso de vacío inmediatamente
                if (newVacuum?.id) {
                  await api
                    .from('process_records')
                    .update({ 
                      status: 'in_progress', 
                      started_at: new Date().toISOString() 
                    })
                    .eq('id', newVacuum.id);
                }
                toast.success('Proceso de Vacío iniciado');
              } else {
                // Dejar pendiente para otro operario y notificar supervisor
                toast.info('Proceso de Vacío pendiente. Se notificará al supervisor.');
                // Crear notificación para supervisor
                try {
                  await api.from('notifications').insert({
                    type: 'process_handoff',
                    title: 'Proceso de Vacío pendiente de asignación',
                    message: 'El operario ' + (profile?.full_name || user?.email) + ' ha completado el laminado y el proceso de Vacío requiere asignación.',
                    target_role: 'supervisor',
                    reference_type: 'process_record',
                    reference_id: newVacuum?.id,
                    is_read: false,
                  });
                } catch (notifErr) {
                  console.log('Tabla notifications no existe, omitiendo notificación');
                }
              }
            } else {
              toast.success('Laminado completado');
            }
          } catch (err) {
            console.error('Error al crear proceso de vacío:', err);
            toast.success('Laminado completado');
          }
          
          setShowLaminationView(false);
          setLaminationWorkOrderId(null);
          refetch();
        }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Module Tabs */}
      <div className="bg-card border-b border-border p-4">
        <div className="max-w-7xl mx-auto">
          <ModuleTabs
            tabs={[
              { value: 'mitrabajo', label: 'Mi Trabajo', href: '/my-work' },
              { value: 'vistaoperario', label: 'Vista Operario', href: '/operator' },
            ]}
          />
        </div>
      </div>

      {/* Header */}
      <header className="bg-card border-b border-border p-4">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="flex items-center gap-3">
            <img 
              src="/madelmold-logo.png" 
              alt="Logo" 
              className="h-10 w-auto"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
            <h1 className="text-xl font-bold text-foreground">
              Magma Composites
            </h1>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/dashboard")}
          >
            <ClipboardList className="w-5 h-5" />
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto p-4 md:p-6">
        {/* Offline Banner */}
        {(isOfflineData || !isOnline) && (
          <Alert variant="default" className="mb-4 bg-amber-500/10 border-amber-500/30">
            <CloudOff className="h-4 w-4 text-amber-600" />
            <AlertDescription className="flex items-center justify-between">
              <span className="text-amber-700 dark:text-amber-400">
                {!isOnline
                  ? "Sin conexión - mostrando datos guardados localmente"
                  : "Mostrando datos en caché"}
                {pendingCount > 0 && ` (${pendingCount} cambios pendientes)`}
              </span>
              {isOnline && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => refetch()}
                  className="ml-2"
                >
                  <RefreshCw className="w-4 h-4 mr-1" />
                  Actualizar
                </Button>
              )}
            </AlertDescription>
          </Alert>
        )}

        <h2 className="text-2xl md:text-3xl font-bold text-center mb-6 text-foreground">
          {t.operatorView.title}
        </h2>

        {!processRecords || processRecords.length === 0 ? (
          <>
            <Card className="mt-8">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <p className="text-muted-foreground text-lg mb-2">
                  {t.operatorView.noAssignedTasks}
                </p>
                <p className="text-sm text-muted-foreground mb-6">
                  {t.operatorView.noAssignedTasksDesc}
                </p>
                <Button onClick={() => navigate("/work-orders")}>
                  {t.operatorView.pendingWorks}
                </Button>
              </CardContent>
            </Card>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
              {nfcScanCard}
            </div>
          </>
        ) : (
          <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {processRecords.map((record) => (
              <Card
                key={record.id}
                className="hover:shadow-lg transition-all duration-200"
              >
                <CardContent className="p-6">
                  <div className="flex flex-col items-center text-center gap-3">
                    <div className="w-full">
                      <h3 className="text-xl font-bold text-foreground mb-2 flex items-center justify-center gap-2">
                        {record.manufacturing_processes?.name?.toUpperCase() || "PROCESO"}
                        {record._pendingSync && (
                          <CloudOff className="w-4 h-4 text-amber-500" title="Pendiente de sincronizar" />
                        )}
                      </h3>
                      <Badge
                        variant={
                          record.status === "in_progress"
                            ? "default"
                            : record.status === "paused"
                            ? "secondary"
                            : "outline"
                        }
                        className="mb-2"
                      >
                        {t.statuses[record.status as keyof typeof t.statuses]}
                      </Badge>
                    </div>
                    <div className="w-full text-sm text-muted-foreground space-y-1">
                      <p className="font-medium">
                        {record.work_orders?.order_number}
                      </p>
                      <p className="text-xs">
                        {record.work_orders?.product_name}
                      </p>

                      {/* Información del molde */}
                      {moldMap[record.work_orders?.mold_id] && (
                        <div className="mt-2 p-2 bg-muted/50 rounded-md text-left">
                          <p className="text-xs font-medium text-foreground flex items-center gap-1">
                            <Box className="w-3 h-3" />
                            Molde: {moldMap[record.work_orders?.mold_id]?.name}
                          </p>
                          {moldMap[record.work_orders?.mold_id]?.location && (
                            <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                              <MapPin className="w-3 h-3" />
                              {moldMap[record.work_orders?.mold_id]?.location}
                            </p>
                          )}
                        </div>
                      )}

                      {/* Plybook info */}
                      {plybookMap[record.work_orders?.plybook_id] && (
                        <div className="mt-1 text-xs text-primary flex items-center justify-center gap-1">
                          <BookOpen className="w-3 h-3" />
                          {plybookMap[record.work_orders?.plybook_id]?.name}
                        </div>
                      )}
                    </div>
                    <div className="w-full flex flex-col gap-2 mt-2">
                      {canManualActions ? (
                        <Button
                          onClick={() => handleProcessAction(record)}
                          className="w-full"
                          variant={record.status === "completed" ? "outline" : "default"}
                          disabled={record.status === "completed" || updateProcess.isPending}
                        >
                          {updateProcess.isPending ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Procesando...
                            </>
                          ) : (
                            <>
                              {record.status === "pending" && (
                                <>
                                  <Play className="w-4 h-4 mr-2" />
                                  Iniciar
                                </>
                              )}
                              {record.status === "in_progress" && (
                                <>
                                  <CheckCircle className="w-4 h-4 mr-2" />
                                  Completar
                                </>
                              )}
                              {record.status === "paused" && (
                                <>
                                  <Play className="w-4 h-4 mr-2" />
                                  Reanudar
                                </>
                              )}
                              {record.status === "completed" && "Completado"}
                            </>
                          )}
                        </Button>
                      ) : (
                        <Button
                          onClick={() => handleProcessAction(record)}
                          className="w-full"
                        >
                          {record.status === "pending" ? (
                            <>
                              <Play className="w-4 h-4 mr-2" />
                              Iniciar
                            </>
                          ) : record.status === "in_progress" ? (
                            <>
                              <CheckCircle className="w-4 h-4 mr-2" />
                              Completar
                            </>
                          ) : (
                            "Ver"
                          )}
                        </Button>
                      )}

                      {/* Ver Plybook button */}
                      {plybookMap[record.work_orders?.plybook_id] && isLaminationProcess(record) && (
                        <Button
                          onClick={() => {
                            setLaminationWorkOrderId(record.work_order_id);
                            setShowLaminationView(true);
                          }}
                          variant="secondary"
                          size="sm"
                          className="w-full"
                        >
                          <BookOpen className="w-4 h-4 mr-2" />
                          Ver Plybook
                        </Button>
                      )}

                      {isSupported && canWriteTags && record.status !== "completed" && (
                        <Button
                          onClick={() => handleWriteNFCTag(record.process_id || record.manufacturing_process_id || record.id, record.work_order_id)}
                          variant="outline"
                          size="sm"
                          className="w-full"
                        >
                          <Radio className="w-4 h-4 mr-2" />
                          Escribir NFC
                        </Button>
                      )}
                      <Button
                        onClick={() => handleProcessClick(record)}
                        variant="ghost"
                        size="sm"
                        className="w-full"
                      >
                        Ver Detalles
                      </Button>

                      {/* Indicador visual para proceso de vacío en progreso */}
                      {isVacuumProcess(record) && record.status === "in_progress" && (
                        <div className="flex items-center justify-center gap-2 pt-2 text-blue-600">
                          <Wind className="w-4 h-4 animate-pulse" />
                          <span className="text-sm font-medium">Vacío en proceso...</span>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}

            {nfcScanCard}
          </div>

          {/* Trabajo disponible en el pool */}
          {poolWork && poolWork.length > 0 && (
            <div className="mt-8">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Hand className="w-5 h-5" />
                Trabajo Disponible
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {poolWork.map((record: any) => (
                  <Card
                    key={record.id}
                    className="hover:shadow-lg transition-all duration-200 border-dashed"
                  >
                    <CardContent className="p-4">
                      <div className="flex flex-col gap-2">
                        <div className="flex items-center justify-between">
                          <Badge variant="outline" className="text-xs">
                            {record.manufacturing_processes?.name || "Proceso"}
                          </Badge>
                          <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-700">
                            Pool
                          </Badge>
                        </div>
                        <p className="font-medium text-sm">
                          {record.work_orders?.order_number}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {record.work_orders?.product_name}
                        </p>

                        {moldMap[record.work_orders?.mold_id] && (
                          <p className="text-xs flex items-center gap-1">
                            <Box className="w-3 h-3" />
                            Molde: {moldMap[record.work_orders?.mold_id]?.name}
                          </p>
                        )}

                        <Button
                          onClick={() => claimWork.mutate({ recordId: record.id })}
                          disabled={claimWork.isPending}
                          size="sm"
                          className="mt-2"
                        >
                          {claimWork.isPending ? (
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Hand className="w-4 h-4 mr-2" />
                          )}
                          Tomar Trabajo
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
          </>
        )}

        {/* Additional Options */}
        <div className="mt-6 flex justify-center gap-4">
          <Button
            variant="outline"
            size="lg"
            onClick={() => navigate("/work-orders")}
            className="w-full sm:w-auto"
          >
            <ClipboardList className="w-5 h-5 mr-2" />
            {t.operatorView.pendingWorks}
          </Button>
        </div>
      </main>

      {/* NFC Dialog */}
      {showNFCDialog && (
        <div className="fixed inset-0 bg-gradient-to-br from-background/95 via-background/90 to-primary/10 backdrop-blur-md z-50 flex items-center justify-center animate-in fade-in duration-300">
          <Card className="w-full max-w-md mx-4 glass-card border-primary/30 shadow-magma">
            <CardContent className="p-8 md:p-12">
              <div className="flex flex-col items-center text-center gap-6">
                {/* Animated NFC Icon */}
                <div className="relative w-32 h-32 flex items-center justify-center">
                  {/* Outer pulsing rings */}
                  {isScanning && (
                    <>
                      <div className="absolute inset-0 rounded-full bg-gradient-magma opacity-20 animate-ping" style={{ animationDuration: '2s' }}></div>
                      <div className="absolute inset-4 rounded-full bg-gradient-magma opacity-30 animate-ping" style={{ animationDuration: '1.5s', animationDelay: '0.3s' }}></div>
                      <div className="absolute inset-8 rounded-full bg-gradient-magma opacity-40 animate-ping" style={{ animationDuration: '1s', animationDelay: '0.6s' }}></div>
                    </>
                  )}
                  
                  {/* Central icon with gradient */}
                  <div className={`relative z-10 ${isScanning ? 'animate-pulse' : ''}`}>
                    <div className="absolute inset-0 bg-gradient-magma blur-xl opacity-50"></div>
                    <Radio className="w-20 h-20 text-transparent bg-gradient-magma bg-clip-text relative z-10" style={{ filter: 'drop-shadow(0 0 8px rgba(156, 39, 176, 0.5))' }} />
                  </div>
                </div>

                {/* Title with gradient */}
                <div className="space-y-2">
                  <h3 className="text-3xl font-bold bg-gradient-magma bg-clip-text text-transparent">
                    {isScanning ? "Escaneando..." : "Escaneo NFC"}
                  </h3>
                  {isScanning && (
                    <div className="flex items-center justify-center gap-1">
                      <div className="w-2 h-2 rounded-full bg-magma-orange animate-bounce" style={{ animationDelay: '0s' }}></div>
                      <div className="w-2 h-2 rounded-full bg-magma-purple animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                      <div className="w-2 h-2 rounded-full bg-magma-orange animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                    </div>
                  )}
                </div>

                {/* Status message */}
                <div className="space-y-2">
                  <p className="text-lg font-medium text-foreground">
                    {isScanning 
                      ? "📱 Acerca tu dispositivo a una etiqueta NFC..."
                      : t.operatorView.waitingTag}
                  </p>
                  {isScanning && (
                    <p className="text-sm text-muted-foreground animate-pulse">
                      Mantén el dispositivo cerca hasta que vibre
                    </p>
                  )}
                </div>

                {/* Support warning */}
                {!isSupported && (
                  <div className="px-4 py-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                    <p className="text-sm text-destructive font-medium">
                      ⚠️ NFC solo disponible en Android con Chrome
                    </p>
                  </div>
                )}

                {/* Cancel button */}
                <Button
                  onClick={handleStopScanning}
                  variant="outline"
                  size="lg"
                  className="mt-4 w-full border-primary/30 hover:bg-primary/10 hover:border-primary/50 transition-all duration-300"
                >
                  {isScanning ? "Detener Escaneo" : "Cancelar"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Botón flotante para reportar incidencias */}
      <Button
        onClick={() => {
          setSelectedWorkOrderForIncident(undefined);
          setShowIncidentDialog(true);
        }}
        className="fixed bottom-6 right-6 h-14 w-14 rounded-full shadow-lg bg-amber-500 hover:bg-amber-600 z-50"
        size="icon"
        title="Reportar Incidencia"
      >
        <AlertTriangle className="h-6 w-6" />
      </Button>

      {/* Diálogo de reporte de incidencias */}
      <IncidentReportDialog
        open={showIncidentDialog}
        onOpenChange={setShowIncidentDialog}
        workOrderId={selectedWorkOrderForIncident}
      />

      {/* Dialog para iniciar proceso de vacío */}
      <Dialog open={showVacuumDialog} onOpenChange={setShowVacuumDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wind className="w-5 h-5 text-blue-500" />
              Iniciar Proceso de Vacío
            </DialogTitle>
            <DialogDescription>
              Ingresa los datos del proceso de vacío para la orden {vacuumPendingRecord?.work_orders?.order_number}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="pressure">Presión de Vacío (mbar o %)</Label>
              <Input
                id="pressure"
                placeholder="Ej: -0.9 bar, 90%, etc."
                value={vacuumData.pressure}
                onChange={(e) => setVacuumData(prev => ({ ...prev, pressure: e.target.value }))}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="observations">Observaciones (opcional)</Label>
              <Textarea
                id="observations"
                placeholder="Notas sobre el proceso de vacío..."
                value={vacuumData.observations}
                onChange={(e) => setVacuumData(prev => ({ ...prev, observations: e.target.value }))}
                rows={3}
              />
            </div>

            <div className="bg-blue-50 dark:bg-blue-950 p-3 rounded-md">
              <p className="text-sm text-blue-700 dark:text-blue-300">
                ⏱️ El tiempo mínimo de vacío es de <strong>{VACUUM_MIN_TIME_MINUTES} minutos</strong>.
                Se mostrará una advertencia si intenta completar antes.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowVacuumDialog(false);
              setVacuumPendingRecord(null);
              setVacuumData({ pressure: "", observations: "" });
            }}>
              Cancelar
            </Button>
            <Button onClick={handleVacuumStart}>
              <Play className="w-4 h-4 mr-2" />
              Iniciar Vacío
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Alerta de tiempo mínimo de vacío */}
      <AlertDialog open={showMinTimeWarning} onOpenChange={setShowMinTimeWarning}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Tiempo Mínimo No Alcanzado
            </AlertDialogTitle>
            <AlertDialogDescription>
              El proceso de vacío lleva solo <strong>{vacuumElapsedMinutes} minutos</strong>.
              El tiempo mínimo recomendado es de <strong>{VACUUM_MIN_TIME_MINUTES} minutos</strong>.
              <br /><br />
              ¿Está seguro de que desea completar el proceso de todas formas?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setShowMinTimeWarning(false);
              setVacuumPendingRecord(null);
            }}>
              Continuar Vacío
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleForceCompleteVacuum} className="bg-amber-600 hover:bg-amber-700">
              Completar de Todas Formas
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialogo de detalles del proceso */}
      <ProcessDetailsDialog
        open={showDetailsDialog}
        onOpenChange={setShowDetailsDialog}
        processRecord={selectedRecordForDetails}
        onOpenLamination={() => {
          if (selectedRecordForDetails?.work_order_id) {
            setLaminationWorkOrderId(selectedRecordForDetails.work_order_id);
            setShowLaminationView(true);
            setShowDetailsDialog(false);
          }
        }}
      />
    </div>
  );
}
